import React, { CSSProperties } from 'react';
import { Stack } from '@chakra-ui/react';
import Plugin, { PluginProps } from '../Plugin';
import Carousel from '../../../../../components/Carousel';
import classNames from 'classnames';
import { useIsMobile } from '../../../../../hooks/isMobile';
import { useIsTablet } from '../../../../../hooks/isTablet';

const mapType = (type: string) => {
  if (type === 'col') return 'column';
  return 'row';
};

const getLayoutStyle = (type: string, gap?: string, isMobile?: boolean, isTablet?: boolean): CSSProperties => {
  const baseStyle: CSSProperties = {
    gap: gap,
  };

  const mobileStyle: CSSProperties = {
    ...baseStyle,
    display: "flex",
    flexDirection: "column",
    flexWrap: "wrap",
  };

  const tabletStyle: CSSProperties = {
    ...baseStyle,
    display: "grid",
    gridTemplateColumns: "1fr 1fr",
    gridTemplateRows: "1fr 1fr",
  };

  if (isMobile) {
    return mobileStyle;
  }

  if (isTablet && !isMobile) {
    if (type === '3x3' || type === '4x4') {
      return tabletStyle;
    }
  }

  switch (type) {
    case "row":
      return {
        ...baseStyle,
        display: "flex",
        flexDirection: "row",
        flexWrap: "wrap",
      };
    case "col":
      return {
        ...baseStyle,
        display: "flex",
        flexDirection: "column",
        flexWrap: "wrap",
      };
    case "2x2":
      return {
        ...baseStyle,
        display: "grid",
        gridTemplateColumns: "1fr 1fr",
        gridTemplateRows: "1fr 1fr",
      };
    case "3x3":
      return {
        ...baseStyle,
        display: "grid",
        gridTemplateColumns: "1fr 1fr 1fr",
        gridTemplateRows: "1fr 1fr 1fr",
      };
    case "4x4":
      return {
        ...baseStyle,
        display: "grid",
        gridTemplateColumns: "1fr 1fr 1fr 1fr",
        gridTemplateRows: "1fr 1fr 1fr 1fr",
      };
    default:
      return {
        ...baseStyle,
        display: "flex",
        flexDirection: "row",
      };
  }
};

const StackLayout = (props: PluginProps) => {
  const { content } = props;
  const isMobile = useIsMobile();
  const isTablet = useIsTablet();

  if (content.data.type === 'car')
    return (
      <Carousel>
        {content.children.map((child, index) => {
          return <Plugin key={index} {...props} content={child} />;
        })}
      </Carousel>
    );

  const direction =
    isMobile && content.data.type === 'row'
      ? 'column'
      : mapType(content.data.type);

  const layoutStyle = getLayoutStyle(content.data.type, content.data.gap, isMobile, isTablet);

  return (
    <Stack
      sx={{
        '& > *': {
          flex: '1 1 0%',
          minWidth: 0,
          minHeight: 0,
          maxWidth: '100%',
          maxHeight: '100%',
        },
        ...(isMobile && {
          '& img': {
            maxWidth: '100%',
            height: 'auto',
          },
          '& h1': {
            fontSize: '1.2rem !important',
          },
          '& h3': {
            fontSize: '1.1rem !important',
          },
        }),
      }}
      spacing={isMobile ? 2 : 0}
      className={classNames({
        left: content.data.justify_self === 'left',
        right: content.data.justify_self === 'right',
        center: content.data.justify_self === 'center',
        wrap: content.data.wrap,
        mobile: isMobile,
      })}
      direction={direction}
      style={{
        ...layoutStyle,
        width: isMobile ? '100%' : content.data.width,
        maxWidth: '100%',
        height: content.data.height,
        gap: isMobile ? '20px' : content.data.gap,
        justifySelf: content.data.justify_self,
        justifyContent: content.data.justify_content,
        alignItems: content.data.align_items,
        padding: isMobile ? '10px' : content.data.padding,
        overflow: 'auto',
        overflowX: 'auto',
        overflowY: 'hidden',
        position: 'relative',
        // boxSizing: 'border-box',
        whiteSpace: 'nowrap',
      }}
    >
      {content.children.map((child, index) => {
        return <Plugin key={index} {...props} content={child} />;
      })}
    </Stack>
  );
};

export default StackLayout;
